import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  CircularProgress,
} from '@mui/material';
import type { EventWaiverItem } from '../../types';
import submitEventWaiver from '../../hooks/waivers/submitEventWaiver';

interface WaiverAcceptModalProps {
  open: boolean;
  waiver: EventWaiverItem | null;
  onClose: () => void;
  onAccepted: () => void;
  /** When true, only show waiver content and Close (e.g. for completed waivers). */
  readOnly?: boolean;
}

const CONFIRM_MESSAGE =
  'I have read and accept the terms of this waiver.';

export function WaiverAcceptModal({
  open,
  waiver,
  onClose,
  onAccepted,
  readOnly = false,
}: WaiverAcceptModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);

  useEffect(() => {
    if (open) {
      setConsentChecked(false);
      setError(null);
    }
  }, [open, waiver?.childId, waiver?.eventSetting?.id]);

  const handleAccept = async () => {
    if (!waiver || !consentChecked) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await submitEventWaiver({
        childId: waiver.childId,
        clientSettingId: waiver.eventSetting.id,
        consentAccepted: true,
      });
      if (result.success) {
        onAccepted();
        onClose();
      } else {
        setError(result.message ?? 'Failed to submit waiver.');
      }
    } catch {
      setError('Unable to submit waiver. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setConsentChecked(false);
      setError(null);
      onClose();
    }
  };

  if (!waiver) return null;

  const contentHtml = waiver.eventSetting?.data ?? '';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>{waiver.templateName}</DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            '& p': { mb: 1.5 },
            '& strong': { fontWeight: 700 },
          }}
        >
          {contentHtml ? (
            <Box
              component="div"
              sx={{ color: 'text.primary', fontSize: 'body2' }}
              dangerouslySetInnerHTML={{ __html: contentHtml }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No waiver content to display.
            </Typography>
          )}
        </Box>

        {!readOnly && (
          <Typography
            variant="body2"
            component="div"
            sx={{
              mt: 2,
              display: 'flex',
              alignItems: 'center',
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            <Checkbox
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              disabled={submitting}
              sx={{ p: 0.5, mr: 0.5, flexShrink: 0 }}
            />
            {CONFIRM_MESSAGE}
          </Typography>
        )}

        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {readOnly ? (
          <Button onClick={handleClose} variant="contained" sx={{ textTransform: 'none' }}>
            Close
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAccept}
              disabled={submitting || !consentChecked}
              sx={{ textTransform: 'none' }}
            >
              {submitting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Submitting…
                </>
              ) : (
                'Accept'
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
